import React, { Suspense, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import CenteredSpinner from "@/shared/components/centered-spinner";
import CustomIcon from "../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useMembership from "@/shared/hooks/useMembership";
import useGetPathPickRequests from "@/features/pathpick/features/sync-download-history/hooks/query/useGetPathPickRequests";
import useGetPathPickCounts from "@/features/pathpick/hooks/query/useGetPathPickCounts";
import PathPickPagination from "@/features/pathpick/components/ui/PathPickPagination";
import PathPickSectionCard from "@/features/pathpick/components/ui/PathPickSectionCard";
import PathPickSyncDownloadHistoryHeader from "@/features/pathpick/features/sync-download-history/components/PathPickSyncDownloadHistoryHeader";
import PathPickUsageCardSlot from "@/features/pathpick/components/usage/PathPickUsageCardSlot";
import PathPickUsageModal from "@/features/pathpick/components/usage/PathPickUsageModal";
import PathPickSyncDownloadHistoryList from "@/features/pathpick/features/sync-download-history/components/PathPickSyncDownloadHistoryList";
import PathPickSyncDownloadProductRequestsModal from "@/features/pathpick/features/sync-download-history/components/PathPickSyncDownloadProductRequestsModal";
import PathPickRequestProductsModal from "@/features/pathpick/components/request/PathPickRequestProductsModal";
import useRequestProductsModalParams from "@/features/pathpick/features/sync-download-history/hooks/useRequestProductsModalParams";
import { Helmet } from "react-helmet-async";

function PathPickSyncDownloadHistoryPageContent() {
  const { t } = useI18nHelper();
  // API 연동 미허용=엑셀 다운로드 관점 / 허용=API 연동 관점 (안내 문구가 갈린다)
  const { isApiSync } = useMembership();

  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [isProductRequestsOpen, setIsProductRequestsOpen] =
    useState<boolean>(false);
  // 모바일 사용량 모달 — 좁은 폭에서 사용량 카드 대신 이걸 띄운다.
  const [isUsageModalOpen, setIsUsageModalOpen] = useState<boolean>(false);
  // 신청 상품 내역 모달은 링크로 공유할 수 있어야 해 열림 상태·검색어를 URL에 둔다(requestId가 null이면 닫힘).
  // 모달이 요청 헤더까지 /requests/{id} 응답에서 읽으므로 요청 행 전체를 들고 있지 않는다.
  const {
    requestId: syncedProductsRequestId,
    query: syncedProductsQuery,
    open: openSyncedProducts,
    close: closeSyncedProducts,
    commitQuery: commitSyncedProductsQuery,
  } = useRequestProductsModalParams();

  const { pathPickRequests, isPending, isPlaceholderData } =
    useGetPathPickRequests(
      { pageNumber, pageSize },
      { pollWhilePending: true }
    );
  const items = pathPickRequests?.items ?? [];
  const total = pathPickRequests?.totalCount ?? 0;

  // 상품별 처리 내역 배지 — 연동/다운로드 요청된 서로 다른 상품 수
  const { pathPickCounts } = useGetPathPickCounts();
  const productRequestCount = pathPickCounts?.request.productCount ?? 0;

  const handleChangePageSize = (size: number) => {
    setPageSize(size);
    setPageNumber(1);
  };

  return (
    <Flex flexDirection={"column"} gap={"16px"}>
      {/* 페이지 타이틀 + 티어별 안내 문구 */}
      <PathPickSyncDownloadHistoryHeader isApiSync={isApiSync} />

      <PathPickSectionCard
        background={"#FFFFFF"}
        header={
          /* 사용량 카드(좌) / 상품별 처리 내역(우) */
          <Flex
            justifyContent={"space-between"}
            alignItems={"center"}
            gap={"16px"}
            /* md 이상에서 카드가 폭에 안 맞으면 버튼이 아래 줄로 내려간다. */
            flexWrap={"wrap"}
          >
            {/* 좁은 폭에서 카드는 한 줄을 다 쓰고 내부까지 세로로 스택되며 상단을
                150px 넘게 잡아먹어 목록을 밀어낸다. 모바일에서는 카드를 감추고
                아래 사용량 버튼 + 모달로 같은 내용을 본다.
                조건부 언마운트가 아니라 CSS로 숨기는 이유는 카드의 usage 쿼리가
                suspense라, 모달을 처음 열 때 페이지 전체가 suspend되는 것을 막기 위함이다. */}
            <Flex
              display={{ base: "none", md: "flex" }}
              minWidth={0}
              flexShrink={1}
            >
              {/* 사용량 카드 / 만료 카드 중 무엇이 들어갈지는 슬롯이 정한다 —
                  모바일 사용량 모달도 같은 슬롯을 쓴다. */}
              <PathPickUsageCardSlot />
            </Flex>
            <PathPickUsageModal.TriggerButton
              onClick={() => setIsUsageModalOpen(true)}
            />
            <AppButton
              variant={"white"}
              onClick={() => setIsProductRequestsOpen(true)}
              chakraProps={{ flexShrink: 0 }}
            >
              <Flex gap={"6px"} alignItems={"center"}>
                <CustomIcon
                  src={"document-text"}
                  width={16}
                  height={16}
                  color={"#444440"}
                />
                <Text textStyle={"body-sm-bold"}>
                  {t("path_pick:sync_download_history.product_requests_button")}
                </Text>
                <Flex
                  minWidth={"20px"}
                  height={"20px"}
                  padding={"0 6px"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  borderRadius={"6px"}
                  background={"#EFEFED"}
                >
                  <Text textStyle={"caption-bold"} color={"#444440"}>
                    {productRequestCount}
                  </Text>
                </Flex>
              </Flex>
            </AppButton>
          </Flex>
        }
      >
        {/* 카드 본문 Box에는 gap이 없어 리스트와 페이지네이션을 직접 묶어준다. */}
        <Flex flexDirection={"column"} gap={"12px"}>
          {isPending ? (
            <CenteredSpinner padding={"48px 0"} />
          ) : (
            <Box position={"relative"}>
              <PathPickSyncDownloadHistoryList
                items={items}
                onViewSyncedProducts={(item) => openSyncedProducts(item.id)}
              />
              {/* 페이지 전환 중에는 이전 목록 위에 오버레이 스피너만 덮는다 */}
              {isPlaceholderData && (
                <Box
                  position={"absolute"}
                  inset={0}
                  background={"rgba(255, 255, 255, 0.6)"}
                  borderRadius={"8px"}
                >
                  <CenteredSpinner />
                </Box>
              )}
            </Box>
          )}
          {total > 0 && (
            <PathPickPagination
              total={total}
              pageNumber={pageNumber}
              pageSize={pageSize}
              onChangePage={setPageNumber}
              onChangePageSize={handleChangePageSize}
            />
          )}
        </Flex>
      </PathPickSectionCard>

      {isUsageModalOpen && (
        <PathPickUsageModal isOpen onClose={() => setIsUsageModalOpen(false)} />
      )}
      {isProductRequestsOpen && (
        <PathPickSyncDownloadProductRequestsModal
          isOpen
          onClose={() => setIsProductRequestsOpen(false)}
        />
      )}
      {syncedProductsRequestId != null && (
        <PathPickRequestProductsModal
          isOpen
          onClose={closeSyncedProducts}
          requestId={syncedProductsRequestId}
          initialQuery={syncedProductsQuery}
          onQueryCommit={commitSyncedProductsQuery}
        />
      )}
    </Flex>
  );
}

function PathPickSyncDownloadHistoryPage() {
  const { tTitle } = useI18nHelper();
  const pageTitle = `${tTitle("common:navigation.path_pick")} | ${tTitle(
    "common:navigation.path_pick_sync_download_history"
  )}`;

  return (
    <Box
      sx={{
        padding: "16px",
        overflow: "auto",
        height: "100%",
        paddingBottom: "100px",
      }}
    >
      <Helmet>
        <title>{pageTitle}</title>
        <meta property="og:title" content={pageTitle} />
      </Helmet>
      {/* 사용량 카드의 suspense 쿼리(usage) 경계 */}
      <Suspense fallback={<CenteredSpinner />}>
        <PathPickSyncDownloadHistoryPageContent />
      </Suspense>
    </Box>
  );
}

export default PathPickSyncDownloadHistoryPage;
